import { motion } from 'framer-motion';
import { ArrowUpRight, Diamond } from 'lucide-react';
import { SPECIMENS, META } from './data';

// HOME — pure-black velvet "showcase". A SINGLE jewel card centred on screen,
// with quiet metadata around it. NO grid of products on the homepage.
// Below: a jewel-on-velvet macro band. Champagne text on near-black.
export default function Home({ setPage }) {
  return (
    <div className="bg-[#0B0A0E] text-white">
      {/* Pitch-black hero with single specimen */}
      <section className="relative pt-32 pb-32 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(201,182,139,0.18),transparent_55%)]" />
        <div className="relative max-w-[1500px] mx-auto px-8 lg:px-14 grid lg:grid-cols-[1fr_1fr] gap-16 items-center">
          <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 1.2 }}>
            <p className="font-mono text-[10px] tracking-[0.4em] uppercase text-[#C9B68B] mb-6">— Atelier · est. 1968</p>
            <h1 className="font-serif text-[56px] sm:text-[88px] lg:text-[140px] leading-[0.85] tracking-[-0.02em]">
              Two stones,<br /><span className="italic text-[#C9B68B]">a year</span>.
            </h1>
            <p className="font-serif italic text-xl lg:text-2xl text-white/70 mt-10 max-w-md leading-[1.4]">{META.motto}</p>
            <div className="mt-12 flex items-center gap-6">
              <button onClick={() => setPage('commission')} className="bg-[#C9B68B] text-[#0B0A0E] px-9 py-4 font-mono text-[10px] tracking-[0.32em] uppercase hover:bg-white transition-colors" data-testid="jewel-home-commission-cta">Commission a piece</button>
              <button onClick={() => setPage('specimens')} className="font-mono text-[10px] tracking-[0.28em] uppercase text-white/70 underline-offset-8 hover:text-[#C9B68B] hover:underline">See the specimens</button>
            </div>
          </motion.div>
          {/* Single specimen card — black velvet on plinth */}
          <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.4, delay: 0.2 }} className="relative">
            <div className="relative aspect-[4/5] overflow-hidden bg-gradient-to-b from-[#1A1820] to-[#0B0A0E] border border-[#C9B68B]/10">
              <img src={SPECIMENS[0].img} alt="" className="w-full h-full object-cover opacity-90" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B0A0E] via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6 lg:p-8">
                <p className="font-mono text-[9px] tracking-[0.4em] uppercase text-[#C9B68B]/85" dangerouslySetInnerHTML={{ __html: SPECIMENS[0].n }} />
                <p className="font-serif text-2xl lg:text-3xl mt-1">{SPECIMENS[0].name}</p>
                <p className="font-mono text-[11px] text-white/55 mt-2">{SPECIMENS[0].stone}</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Pull quote in champagne on black */}
      <section className="border-y border-[#C9B68B]/10">
        <div className="max-w-[900px] mx-auto px-8 lg:px-14 py-24 text-center">
          <Diamond className="w-5 h-5 mx-auto text-[#C9B68B] mb-8 rotate-45" strokeWidth={1.2} fill="currentColor" />
          <p className="font-serif text-3xl lg:text-5xl leading-[1.2] italic text-white">&ldquo;A great stone is a small lifetime of light. Our work is to find a setting that does not get in its way.&rdquo;</p>
          <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-[#C9B68B] mt-10">— Klara Lumen, Maître Joaillier</p>
        </div>
      </section>

      {/* Jewel-on-velvet macro band — wide image with caption layered, NOT card grid */}
      <section className="relative overflow-hidden">
        <div className="aspect-[16/7] relative">
          <img src="https://images.pexels.com/photos/177332/pexels-photo-177332.jpeg?auto=compress&cs=tinysrgb&w=2200" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0B0A0E] via-[#0B0A0E]/40 to-[#0B0A0E]/0" />
        </div>
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-[1500px] mx-auto px-8 lg:px-14 w-full">
            <p className="font-mono text-[10px] tracking-[0.4em] uppercase text-[#C9B68B] mb-4">— A bracelet, 18 months</p>
            <h2 className="font-serif text-4xl lg:text-7xl max-w-2xl leading-[0.95]">Twenty-four <span className="italic text-[#C9B68B]">stones</span>.<br />Twenty-four <span className="italic text-[#C9B68B]">months</span>.</h2>
            <p className="text-base text-white/65 mt-6 max-w-md leading-[1.6]">The line bracelet on the next page took eighteen months from first conversation to delivery. Two of those months were spent finding stone number eleven.</p>
            <button onClick={() => setPage('specimens')} className="mt-8 inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] uppercase text-[#C9B68B] hover:text-white">View the specimens <ArrowUpRight className="w-3 h-3" /></button>
          </div>
        </div>
      </section>
    </div>
  );
}
